import { Component, computed, HostBinding, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from '@bcef/ia-components/buttons';
import { DatepickerModule } from '@bcef/ia-components/datepicker';
import { InputModule } from '@bcef/ia-components/input';
import { PanelModule } from '@bcef/ia-components/panel';
import { TooltipModule } from '@bcef/ia-components/tooltip';
import { TypologyLiteral } from '@bcef/ia-shared';
import { DataLayered } from '@difa/ia-data-layer';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { ChannelType, ClientType, RoutesPath } from '@features/termination/enums';
import { PersonalInformation } from '@features/termination/interfaces';
import { ErrorMessageComponent } from '@shared/components/error-message/error-message.component';
import { RadioDirective } from '@shared/directives';
import { specialCharactersNotAllowed } from '@shared/utils/not-allowed-characters';

import { TerminationFacade } from '../../termination.facade';
import { PersonalInformationFacade } from './personal-information.facade';

@DataLayered('screens.personalInformation.datalayer')
@Component({
  selector: 'ia-resil-contrats-personal-information',
  templateUrl: './personal-information.component.html',
  imports: [
    ErrorMessageComponent,
    TranslateModule,
    ButtonModule,
    InputModule,
    NgxMaskDirective,
    DatepickerModule,
    TooltipModule,
    PanelModule,
    ReactiveFormsModule,
    RadioDirective,
  ],
  providers: [provideNgxMask()],
})
export class PersonalInformationComponent implements OnInit {
  @HostBinding('class') public readonly classAttribute =
    'tw-mx-0 tw-flex tw-flex-col tw-pb-10 md:tw-mx-24 md:tw-mt-10 md:tw-pb-24 hb-group:tw-mx-7';

  private readonly terminationFacade: TerminationFacade = inject(TerminationFacade);

  private readonly translate: TranslateService = inject(TranslateService);

  private readonly personalInformationFacade: PersonalInformationFacade =
    inject(PersonalInformationFacade);

  public readonly PhysicalPerson = ClientType.PHYSICAL_PERSON;
  public readonly LegalPerson = ClientType.LEGAL_PERSON;

  public readonly Email = ChannelType.EMAIL;
  public readonly Paper = ChannelType.PAPER;

  public readonly isPro = this.terminationFacade.isPro;

  public ngOnInit(): void {
    this.terminationFacade.updateStepInProgress(1);
    this.personalInformationFacade.setPersonalInformationFormDefaultValues();
  }

  public get personalInformationForm(): FormGroup {
    return this.personalInformationFacade.personalInformationForm;
  }

  public get email(): FormControl<string | null> {
    return this.personalInformationFacade.email;
  }

  public get dateOfBirth(): FormControl<string | null> {
    return this.personalInformationFacade.dateOfBirth;
  }

  public get fullName(): FormControl<string | null> {
    return this.personalInformationFacade.fullName;
  }

  public get firstName(): FormControl<string | null> {
    return this.personalInformationFacade.firstName;
  }

  public get gender(): FormControl<number | null> {
    // Directly retrieve the gender control from the form group.
    // This works even when the facade stub does not expose a gender getter.
    return this.personalInformationForm.get('gender') as FormControl<number | null>;
  }

  public get firstNameValue(): string | null {
    // Retrieve the firstName value directly from the form group.
    // This avoids reliance on a facade getter that may be missing in tests.
    return this.personalInformationForm.get('firstName')?.value ?? null;
  }

  public get identificationValue(): string | null {
    return this.personalInformationFacade.identification.value;
  }

  public get applicantLastName(): FormControl<string | null> {
    return this.personalInformationFacade.applicantLastName;
  }

  public get applicantFirstName(): FormControl<string | null> {
    return this.personalInformationFacade.applicantFirstName;
  }

  public get countryOfBirth(): FormControl<string | null> {
    return this.personalInformationFacade.countryOfBirth;
  }

  public get communeOfBirth(): FormControl<string | null> {
    return this.personalInformationFacade.communeOfBirth;
  }

  public get channelType(): FormControl<string | null> {
    return this.personalInformationFacade.channelType;
  }

  public get address(): FormControl<string | null> {
    return this.personalInformationFacade.address;
  }

  public get postalCode(): FormControl<string | null> {
    return this.personalInformationFacade.postalCode;
  }

  public get city(): FormControl<string | null> {
    return this.personalInformationFacade.city;
  }

  public readonly isConnected: boolean = this.personalInformationFacade.isConnected;

  public readonly isMobile: boolean = this.personalInformationFacade.isMobile;

  public readonly isMbGroup: boolean = this.terminationFacade.isMbGroup;

  public readonly isHb: boolean = this.terminationFacade.isHbGroup;

  public specialCharactersNotAllowed = specialCharactersNotAllowed;

  public readonly connectedValues = computed(() => [
    {
      key: this.translate
        .instant('screens.personalInformation.panel.gender.label')
        .replace(':', ''),
      value: this.translate.instant(
        `screens.personalInformation.panel.gender.${this.gender.value === 1 ? 'man' : 'woman'}`,
      ),
    },
    {
      key: this.translate.instant('screens.personalInformation.panel.fullName.label'),
      value: this.fullName.value,
    },
    {
      key: this.translate.instant('screens.personalInformation.panel.firstName.label'),
      value: this.firstName.value,
    },
    {
      key: this.translate.instant('screens.personalInformation.panel.applicantLastName.label'),
      value: this.applicantLastName.value,
    },
    {
      key: this.translate.instant('screens.personalInformation.panel.applicantFirstName.label'),
      value: this.applicantFirstName.value,
    },
    {
      key: this.translate.instant('screens.personalInformation.panel.dateOfBirth.label'),
      value: this.dateOfBirth.value,
    },
    {
      key: this.translate.instant('screens.personalInformation.panel.countryOfBirth.label'),
      value: this.countryOfBirth.value,
    },
    {
      key: this.translate.instant('screens.personalInformation.panel.communeOfBirth.label'),
      value: this.communeOfBirth.value,
    },
  ]);

  public get typology(): TypologyLiteral {
    return this.terminationFacade.typology;
  }

  public onPrevious(): void {
    this.terminationFacade.navigateTo(RoutesPath.ONBOARDING_PAGE);
  }

  public onNext(): void {
    this.personalInformationForm.markAllAsTouched();
    if (this.personalInformationForm.invalid) {
      return;
    }
    const personalInformation: PersonalInformation = this.personalInformationForm.getRawValue();
    this.personalInformationFacade.updatePersonalInformation(personalInformation);
  }
}
